import { SearchAdapter } from '@empathy/search-adapter';
import { PartialResult, RelatedTag, Result } from '@empathy/search-types';
import { Observable } from 'rxjs/Observable';
import { filter, map } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { throttleTime } from 'rxjs/operators/throttleTime';
import { EVENTS } from '../../bus/events.const';
import { container } from '../../container';
import { DEPENDENCIES } from '../../container/dependencies.const';
import { EmpathyXConfig, FacetGroup, FacetsGroups, ScrollDirection } from '../../models';
import { DeviceDetector } from '../../services';
import { StateCacheService } from '../../services/state-cache.service';
import { ACTIONS_KEYS, GETTERS_KEYS, MUTATIONS_KEYS } from '../../store';
import { amendQueryWithRelatedTags } from '../../utils/amend-query-with-related-tags';
import { hasSearchQuery } from '../pipes/has-search-query.operator';
import { createWires } from '../wiring.util';
import { WIRE_FACTORY } from './wires.factory';

export const CORE_WIRES = createWires({
  scrollStopped(observable: Observable<number>, { store, emit }) {
    const { scroll: scrollConfig }: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
    observable
      .pipe(debounceTime(scrollConfig.scrollEventDebounceTimeInMs))
      .subscribe((scrollTop: number) => {
        emit(EVENTS.ScrollStopped, scrollTop);
      });
  },
  changeScrollDirection(observable: Observable<ScrollDirection>, { store }) {
    observable
      .pipe(throttleTime(300, undefined, { leading: true, trailing: true }))
      .subscribe((scrollDirection: ScrollDirection) => store.commit(MUTATIONS_KEYS.core.setScrollDirection, scrollDirection));
  },
  changeScrollOffset(observable: Observable<boolean>, { store }) {
    observable
      .pipe(throttleTime(300, undefined, { leading: true, trailing: true }))
      .subscribe((offset: boolean) => store.commit(MUTATIONS_KEYS.core.setScrollOffset, offset));
  },
  applyScrollIfNeeded(observable: Observable<PartialResult>, { store, emit }) {
    observable.subscribe(() => {
      if (store.state.url.stateFromUrl) {
        emit(EVENTS.RestoreScroll);
        store.commit(MUTATIONS_KEYS.url.setStateFromURL, false);
      }
      if (store.state.core.stateLoadedFromCache) {
        emit(EVENTS.RestoreScroll);
      }
    });
  },
  toggleFacetsGroupPanel(observable: Observable<string>, { store }) {
    observable.subscribe((panelId: string) => {
      const groups: FacetsGroups = store.state.search.facetsGroups;
      const facetPanelGroup: FacetGroup = groups[panelId];
      if (facetPanelGroup) {
        store.commit(MUTATIONS_KEYS.search.setFacetsPanelCollapsed, { panelId: panelId, collapsed: !facetPanelGroup.collapsed });
      }
    });
  },
  handleTabKeyPressed(observable: Observable<KeyboardEvent>, { store }) {
    observable.subscribe(event => {
      const key = event.key || event.keyCode;
      if (!store.state.core.hasTabbed && key === 'Tab' || key === 9) {
        store.commit(MUTATIONS_KEYS.core.setHasTabbed, true);
      }
    });
  },
  emitUserAcceptedQuery(observable, { store, emit }) {
    observable
      .pipe(
        hasSearchQuery(store),
        map(() => {
          const selectedRelatedTags: RelatedTag[] = store.getters[GETTERS_KEYS.querySignals.selectedRelatedTags];
          return amendQueryWithRelatedTags(store.state.search.query, selectedRelatedTags).toLowerCase();
        }),
        filter(amendedQuery => amendedQuery !== store.state.core.userAcceptedQuery.toLowerCase())
      ).subscribe(() => emit(EVENTS.UserAcceptedQueryChanged));
  },
  setUserAcceptedQuery(observable, { store }) {
    observable.subscribe(() => {
      const selectedRelatedTags: RelatedTag[] = store.getters[GETTERS_KEYS.querySignals.selectedRelatedTags];
      store.commit(MUTATIONS_KEYS.core.setUserAcceptedQuery, amendQueryWithRelatedTags(store.state.core.inputQuery, selectedRelatedTags));
    });
  },
  invalidateEmpathyAdapterCache(observable) {
    observable.subscribe(() => {
      const searchAdapter = container.get<SearchAdapter>(DEPENDENCIES.Adapter);
      searchAdapter.invalidateCache?.();
    });
  },
  emitResultsEnd(observable, { store, emit }) {
    observable.subscribe(() => {
      if (!store.state.tracking.resultsEnd
        && store.state.search.numFound > 0
        && !store.getters[GETTERS_KEYS.search.hasMoreResults]) {
        emit(EVENTS.ResultsEnd);
      }
    });
  },
  detectDevice(observable, { store }) {
    const screenWidthDetector = container.get<DeviceDetector>(DEPENDENCIES.ScreenWidthDetector);
    observable
      .pipe(throttleTime(100, undefined, { leading: true, trailing: true }))
      .subscribe(() =>
        store.commit(MUTATIONS_KEYS.config.setDevice, screenWidthDetector.detect(store.state.config.devicesDetectionConfig)));
  },
  saveStoreStateToCache(observable: Observable<Result>, { store }) {
    observable.subscribe(() => {
      container.get<StateCacheService>(DEPENDENCIES.StateCacheService).saveStateToCache(store);
    });
  },
  saveFirstResultIdInView: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setFirstGridItemIdInView),
  disableStateLoadedFromCache: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.stateLoadedFromCache, false),
  addToWishlist: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.addToWishlist),
  calculateCorePosition: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.core.calculateCorePosition),
  callAddToWishlistClientEvent: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.core.callAddToWishlistClientEvent),
  callGetWishlistClientEvent: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.core.callGetWishlistClientEvent),
  callRemoveFromWishlistClientEvent: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.core.callRemoveFromWishlistClientEvent),
  clearInputQuery: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.core.setInputQuery, ''),
  closeEmpathyX: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setOpen, false),
  closeEmpathyXIfNoInputQuery: WIRE_FACTORY.emitEventConditionally({ ifEvent: EVENTS.CloseEmpathyX },
    (_, store) => !!store.state.core.inputQuery),
  closeFacetsGroupPanel: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.closeFacetsPanels),
  disableOpenFinished: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setOpenFinished, false),
  enableOpenFinished: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setOpenFinished, true),
  mapWishlistToExistingResults: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.core.mapWishlistToExistingResults),
  openEmpathyX: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setOpen, true),
  removeFromWishlist: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.removeFromWishlist),
  resetScrollDirection: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setScrollDirection, ScrollDirection.UP),
  resetScrollOffset: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setScrollOffset, false),
  setInputAsClean: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setDirtyInput, false),
  setInputAsDirty: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setDirtyInput, true),
  setScrollEnabled: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setScrollEnabled),
  setHorizontalScrollType: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setHorizontalScrollType),
  setWishlist: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.core.setWishlist),
  scrollToTop: WIRE_FACTORY.emitEvent(EVENTS.ScrollTo, 0)
});
